import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { HelpCircle, MessageCircleQuestion, CheckCircle2, Clock, ArrowRight, BookOpen, ChevronDown } from 'lucide-react';

const COLLAPSE_KEY = 'odys_qa_card_collapsed';

const QuestionsSpotlightCard = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [chapterCount, setChapterCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1');

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      localStorage.setItem(COLLAPSE_KEY, prev ? '0' : '1');
      return !prev;
    });
  };

  useEffect(() => {
    api.get('/questions?limit=3')
      .then((res) => setQuestions((res.data || []).slice(0, 3)))
      .catch(() => setQuestions([]))
      .finally(() => setLoaded(true));
    api.get('/chapters')
      .then((res) => setChapterCount((res.data || []).filter((c) => c.is_chapter !== false).length))
      .catch(() => setChapterCount(0));
  }, []);

  if (!loaded) return null;

  const pending = questions.filter((q) => !q.is_answered).length;

  return (
    <section
      className="my-8 rounded-2xl border border-[#d8c9a7] bg-[#fbf7ef] shadow-[0_2px_14px_rgba(26,35,64,0.06)] overflow-hidden"
      data-testid="questions-spotlight-card"
    >
      <div className="h-1 w-full bg-gradient-to-r from-[#c9a349] via-[#e0c477] to-[#c9a349]" />

      <div className="p-5 sm:p-7">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 lg:max-w-xl">
            <button
              type="button"
              onClick={toggleCollapsed}
              className="w-full flex items-center gap-3 text-left"
              aria-expanded={!collapsed}
              data-testid="questions-spotlight-toggle"
            >
              <span className="w-11 h-11 rounded-xl bg-[#1a2340] text-[#e0c477] flex items-center justify-center shrink-0">
                <MessageCircleQuestion className="w-5 h-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-heading font-bold text-base md:text-lg text-[#1a2340] leading-tight">
                  Une question sur un chapitre de formation ?
                </span>
                <span className="block text-sm text-[#5b6480] mt-0.5">
                  {collapsed
                    ? `${pending} question${pending > 1 ? 's' : ''} en attente · clique pour déplier`
                    : "Pose-la ici, elle sera classée par chapitre et traitée par l'équipage"}
                </span>
              </span>
              <ChevronDown
                className={`w-5 h-5 text-[#5b6480] shrink-0 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
              />
            </button>

            {!collapsed && (
              <>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <Badge variant="outline" className="border-[#c9a349]/50 text-[#8a6d1f] bg-white/60">
                <BookOpen className="w-3 h-3 mr-1" />
                {chapterCount || 18} chapitres
              </Badge>
              {pending > 0 && (
                <Badge variant="outline" className="border-amber-500/40 text-amber-700 bg-white/60">
                  <Clock className="w-3 h-3 mr-1" />
                  {pending} en attente de réponse
                </Badge>
              )}
            </div>

            {questions.length > 0 && (
              <ul className="mt-4 space-y-2">
                {questions.map((q) => (
                  <li key={q.id}>
                    <button
                      type="button"
                      onClick={() => navigate('/questions')}
                      className="w-full text-left flex items-start gap-2.5 rounded-lg px-3 py-2 bg-white/70 border border-[#e4d9bf] hover:border-[#c9a349] transition-colors"
                      data-testid={`spotlight-question-${q.id}`}
                    >
                      {q.is_answered
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                        : <HelpCircle className="w-4 h-4 text-[#c9a349] mt-0.5 shrink-0" />}
                      <span className="min-w-0">
                        <span className="block text-sm text-[#1a2340] line-clamp-1">{q.question}</span>
                        <span className="block text-xs text-[#8a93a8]">
                          {q.chapter_title || 'Question générale'}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {questions.length === 0 && (
              <p className="text-sm text-[#8a93a8] mt-4">
                Aucune question pour le moment — sois le premier à lancer la discussion sur un chapitre.
              </p>
            )}
              </>
            )}
          </div>

          {!collapsed && (
          <div className="flex flex-col gap-2 lg:items-end shrink-0" data-testid="questions-spotlight-actions">
            <Button
              onClick={() => navigate('/questions?ask=1')}
              className="bg-[#1a2340] hover:bg-[#2a3658] text-white"
              data-testid="spotlight-ask-question-btn"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Poser ma question
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/questions')}
              className="text-[#1a2340] hover:bg-[#1a2340]/5"
              data-testid="spotlight-see-all-btn"
            >
              Voir toutes les questions
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default QuestionsSpotlightCard;
